import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { installationAPI } from '../api/installation';
import { toast } from 'react-toastify';
import './Installations.css';

const Installations = () => {
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });

  useEffect(() => {
    fetchInstallations();
  }, [pagination.page]);

  const fetchInstallations = async () => {
    try {
      setLoading(true);
      const response = await installationAPI.getAll(pagination.page, pagination.limit);
      setInstallations(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      toast.error('Failed to fetch installations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (uniqueId, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await installationAPI.delete(uniqueId);
      toast.success('Installation deleted successfully');
      fetchInstallations();
    } catch (error) {
      toast.error('Failed to delete installation');
    }
  };

  const copyUniqueId = (uniqueId) => {
    navigator.clipboard.writeText(uniqueId);
    toast.success('Unique ID copied to clipboard');
  };

  if (loading) {
    return <div className="loading">Loading installations...</div>;
  }

  return (
    <div className="installations-page">
      <div className="page-header">
        <h1>Installations</h1>
        <Link to="/installations/create" className="btn btn-primary">
          + New Installation
        </Link>
      </div>

      {installations.length === 0 ? (
        <div className="empty-state">
          <p>No installations found</p>
          <Link to="/installations/create" className="btn btn-primary">
            Create First Installation
          </Link>
        </div>
      ) : (
        <>
          <div className="installations-grid">
            {installations.map((installation) => (
              <div key={installation.uniqueId} className="installation-card">
                <div className="card-header">
                  <h3>{installation.installationName}</h3>
                  <span className={`status ${installation.isActive ? 'active' : 'inactive'}`}>
                    {installation.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="card-body">
                  {installation.computerName && (
                    <p><strong>Computer:</strong> {installation.computerName}</p>
                  )}
                  {installation.description && (
                    <p><strong>Description:</strong> {installation.description}</p>
                  )}
                  <p><strong>Backup Configs:</strong> {installation.backupConfigCount || 0}</p>
                  
                  <div className="unique-id-section">
                    <strong>Unique ID:</strong>
                    <div className="unique-id-display">
                      <code>{installation.uniqueId}</code>
                      <button
                        onClick={() => copyUniqueId(installation.uniqueId)}
                        className="btn-copy"
                        title="Copy to clipboard"
                      >
                        📋
                      </button>
                    </div>
                  </div>

                  {installation.lastSyncedAt && (
                    <p className="last-sync">
                      <strong>Last Sync:</strong>{' '}
                      {new Date(installation.lastSyncedAt).toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="card-actions">
                  <Link
                    to={`/installations/${installation.uniqueId}`}
                    className="btn btn-secondary"
                  >
                    View Details
                  </Link>
                  <Link
                    to={`/installations/${installation.uniqueId}/backup-config/create`}
                    className="btn btn-success"
                  >
                    + Backup Config
                  </Link>
                  <button
                    onClick={() => handleDelete(installation.uniqueId, installation.installationName)}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn"
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Installations;